import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  AudioLines,
  Mic,
  Search,
} from 'lucide-react';
import api from '../utils/api';
import { VoiceCard } from './voice/VoiceCard';
import VoiceTour, { voiceTourSeen } from './voice/VoiceTour';
import EchoesDashboard, {
  computeAggregates,
  isMeasurable,
  latestPerDay,
  windowEchoes,
} from './EchoesDashboard';

// Re-exported for existing importers: App.js reaches for these, and the
// SegmentedRawText test imports from here. The definitions live in
// ./voice/primitives.
export { normalizeForCompare, PendingEmbedDot, AudioStrip, SegmentedRawText } from './voice/primitives';

const SAGE = '#7C9A82';
const AMBER = '#D4A574';
const POLL_MS = 3000;
const NON_TERMINAL_STATUSES = new Set(['pending', 'transcribed', 'cleaned']);
const NON_TERMINAL_EMBED = new Set(['pending', 'processing']);
function dateHeader(iso) {
  if (!iso) return '';
  const today = new Date();
  const d = new Date(iso);
  const sameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (sameDay(d, today)) return 'Today';
  if (sameDay(d, yesterday)) return 'Yesterday';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function groupByDate(items) {
  const map = new Map();
  for (const item of items) {
    const key = item.entry_date || '';
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(item);
  }
  return [...map.entries()];
}

function PageHeader({
  title = 'Voice Notes',
  subtitle = null,
  Icon = Mic,
  search,
  onSearch,
  searchPlaceholder = 'Search notes...',
  searchAriaLabel = 'Search voice notes',
  onRecord,
  recordRef,
}) {
  return (
    <header className="mb-4 sm:mb-7 space-y-3 sm:space-y-5">
      <div className="flex items-center gap-2.5 sm:gap-3">
        <span className="flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-xl sm:rounded-2xl bg-stone-900 text-white shadow-sm shrink-0">
          <Icon size={18} strokeWidth={2.2} className="sm:hidden" />
          <Icon size={22} strokeWidth={2.2} className="hidden sm:block" />
        </span>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-stone-900 dark:text-[#F0EDE8]">
            {title}
          </h1>
          {subtitle && (
            <p className="text-sm text-stone-500 dark:text-stone-400 leading-relaxed mt-0.5">
              {subtitle}
            </p>
          )}
        </div>
        {onRecord && (
          <button
            ref={recordRef}
            type="button"
            onClick={onRecord}
            aria-label="Record a voice note"
            className="inline-flex items-center gap-2 rounded-full px-4 sm:px-5 py-2.5 text-sm font-semibold text-white shadow-sm shrink-0 transition-all duration-300 ease-out hover:-translate-y-0.5 hover:shadow-md active:scale-95 motion-reduce:transition-none motion-reduce:hover:translate-y-0"
            style={{ backgroundColor: SAGE }}
          >
            <Mic size={16} strokeWidth={2.4} />
            <span className="hidden sm:inline">Speak</span>
          </button>
        )}
      </div>
      <div className="relative">
        <Search
          size={16}
          className="absolute left-4 sm:left-5 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none"
        />
        <input
          type="search"
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          placeholder={searchPlaceholder}
          aria-label={searchAriaLabel}
          className="w-full pl-11 sm:pl-12 pr-4 sm:pr-5 py-3 sm:py-3.5 rounded-2xl bg-white dark:bg-stone-800/60 border border-stone-200 dark:border-stone-700 shadow-sm text-[15px] sm:text-base text-stone-700 dark:text-[#F0EDE8] placeholder:text-stone-400 focus:outline-none focus:border-stone-400 transition-colors"
        />
      </div>
    </header>
  );
}

function EmptyState({ onRecord }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-16 px-6">
      <span
        className="flex h-16 w-16 items-center justify-center rounded-2xl mb-5"
        style={{ backgroundColor: `${SAGE}1A`, color: SAGE }}
      >
        <AudioLines size={28} strokeWidth={1.5} />
      </span>
      <p className="text-base text-stone-700 dark:text-[#F0EDE8] font-medium mb-1.5">
        Some things are easier said than written.
      </p>
      <p className="text-sm text-stone-500 dark:text-stone-400 max-w-[32ch] leading-relaxed mb-5">
        Talk for as long as you like. We'll write it down and keep it with that
        day's journal.
      </p>
      {onRecord && (
        <button
          type="button"
          onClick={onRecord}
          className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white shadow-sm transition-all duration-300 ease-out hover:-translate-y-0.5 hover:shadow-md active:scale-95 motion-reduce:transition-none motion-reduce:hover:translate-y-0"
          style={{ backgroundColor: SAGE }}
        >
          <Mic size={16} strokeWidth={2.4} />
          Speak
        </button>
      )}
    </div>
  );
}

function NoResultsState({ query, onClear }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-12 px-6 gap-3">
      <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-stone-100 dark:bg-stone-800 text-stone-400">
        <Search size={20} strokeWidth={1.5} />
      </span>
      <p className="text-sm text-stone-600 dark:text-stone-300">
        No notes match <span className="font-medium">"{query}"</span>.
      </p>
      <button
        type="button"
        onClick={onClear}
        className="text-xs text-stone-500 hover:text-stone-700 dark:hover:text-stone-200 underline underline-offset-2"
      >
        Clear search
      </button>
    </div>
  );
}

// ---- main --------------------------------------------------------------

/**
 * The Voice tab: one list of everything the user has spoken, newest first.
 *
 * Until 2026-09-23 this was two lists behind Notes / Echoes sub-tabs, each with
 * its own fetch, its own card, its own empty state and its own search. Prod said
 * the split cost more than it bought: of 18 people who ever recorded, **zero**
 * found Echoes without first finding Notes, and the two behaved identically
 * anyway (29s median take on both).
 *
 * `onRecord` opens the capture sheet; `onOpenJournal(entryDate)` jumps to that
 * day's journal entry — the proof, on every card, that speaking produced
 * something durable.
 */
export function VoiceTab({ userId, onRecord, onOpenJournal }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const pollRef = useRef(null);
  const mountedRef = useRef(true);

  // The tour runs once, the first time Voice is opened with nothing recorded.
  // Two conditions, both required: the local flag is instant, and the
  // empty-list check is what stops it re-running on a second device for
  // someone who already has fifty recordings. The list is already in hand, so
  // this costs no extra request.
  const [tourOpen, setTourOpen] = useState(false);
  const tourCheckedRef = useRef(false);
  const recordRef = useRef(null);
  const stripRef = useRef(null);
  const firstCardRef = useRef(null);
  const tourAnchors = useMemo(
    () => ({ record: recordRef, strip: stripRef, card: firstCardRef }),
    []
  );

  // The insight strip is collapsed by default. The full dashboard is taller
  // than a phone screen, and sitting above the list it pushed every recording
  // below the fold — on 2026-09-24 a saved take looked missing from Voice for
  // exactly that reason. The numbers come from the dashboard's own exported
  // helpers, so the one-liner and the full view cannot disagree.
  const [stripOpen, setStripOpen] = useState(false);
  const stripSummary = useMemo(() => {
    const daily = latestPerDay(windowEchoes(items));
    const agg = computeAggregates(daily.filter(isMeasurable));
    if (!agg.count) return null;
    const shift = Math.abs(agg.avgShift) < 0.05 ? 0 : agg.avgShift;
    return { shift, labelled: agg.count, total: daily.length };
  }, [items]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, []);

  const fetchList = useCallback(async () => {
    try {
      // No `kind` filter: one list, both kinds. `kind` survives on the server as
      // provenance for rows captured before the merge, and nothing reads it here.
      const { data } = await api.get('/api/voice', { params: { limit: 50 } });
      if (!mountedRef.current) return data?.items || [];
      setItems(data?.items || []);
      setError(null);
      return data?.items || [];
    } catch (err) {
      if (mountedRef.current) {
        setError(err?.response?.data?.error || err?.message || 'Could not load voice notes');
      }
      return null;
    }
  }, []);

  const scheduleNextPoll = useCallback(
    (currentItems) => {
      if (!mountedRef.current) return;
      // Continue polling while ANY row is in flight — either the transcription
      // / cleanup pipeline (status) OR the embedding pipeline (embedding_status).
      // Without the embedding_status check, polling would stop the moment a
      // voice note hits status='accepted', and the amber pending-embed dot
      // would pulse forever because nothing fetches the eventual
      // embedding_status='complete'.
      //
      // Reading the EFFECTIVE status is what stops a consent-blocked row from
      // polling forever: its raw embedding_status is 'pending', but nothing on
      // the server will ever advance it — only the user turning AI on will, and
      // that reloads this list anyway. 'ai_consent_pending' is not in the
      // non-terminal set, so those rows correctly settle.
      const stillProcessing = (currentItems || []).some(
        (it) =>
          NON_TERMINAL_STATUSES.has(it.status) ||
          NON_TERMINAL_EMBED.has(it.effective_embedding_status ?? it.embedding_status)
      );
      if (!stillProcessing) return;
      if (pollRef.current) clearTimeout(pollRef.current);
      pollRef.current = setTimeout(async () => {
        const next = await fetchList();
        scheduleNextPoll(next || []);
      }, POLL_MS);
    },
    [fetchList]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const list = await fetchList();
      if (!cancelled) {
        setLoading(false);
        scheduleNextPoll(list || []);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchList, scheduleNextPoll]);

  useEffect(() => {
    if (loading || error || tourCheckedRef.current) return;
    tourCheckedRef.current = true;
    if (items.length === 0 && !voiceTourSeen()) setTourOpen(true);
  }, [loading, error, items.length]);

  const queryClient = useQueryClient();

  // Only called after an accept/edit/delete (never the raw polling loop,
  // which calls fetchList directly) — so these two invalidations fire on
  // real changes, not every 3s poll tick. The Home rhythm card's spoken-word
  // count and the Companion card both talk about the latest voice entry.
  const refresh = useCallback(async () => {
    const list = await fetchList();
    scheduleNextPoll(list || []);
    if (userId) {
      queryClient.invalidateQueries({ queryKey: ['journalPulse', userId] });
      queryClient.invalidateQueries({ queryKey: ['companionOpener', userId] });
    }
  }, [fetchList, scheduleNextPoll, queryClient, userId]);

  const handleDeleted = useCallback(
    async (deletedId) => {
      setItems((prev) => prev.filter((it) => it.id !== deletedId));
      await refresh();
    },
    [refresh]
  );


  // Search filter — substring match against raw, cleaned, user_edited, accepted.
  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => {
      const hay = [
        it.raw_transcript,
        it.cleaned_transcript,
        it.user_edited_text,
        it.accepted_text,
      ]
        .filter(Boolean)
        .map((s) => s.toLowerCase())
        .join('');
      return hay.includes(q);
    });
  }, [items, search]);

  const grouped = useMemo(() => groupByDate(filteredItems), [filteredItems]);

  const headerProps = {
    title: 'Voice',
    subtitle: 'A safe space to think out loud.',
    Icon: Mic,
    searchPlaceholder: 'Search everything you\'ve said...',
    searchAriaLabel: 'Search voice notes',
    onRecord,
  };

  const handleMoodChanged = useCallback(
    (id, serverRow) => {
      // PATCH may have flipped embedding_status back to pending — sync that
      // into the list so PendingEmbedDot reappears and polling resumes.
      // Compute the next-items snapshot up front so scheduleNextPoll sees the
      // post-patch embedding_status (setItems is async — passing the closure's
      // `items` would use the stale pre-patch snapshot and polling would exit
      // immediately, stranding the row at 'pending' with the orange dot on).
      const nextItems = items.map((it) =>
        it.id === id
          ? {
              ...it,
              emotion_before: serverRow?.emotion_before ?? it.emotion_before,
              emotion_after: serverRow?.emotion_after ?? it.emotion_after,
              mood_before: serverRow?.mood_before ?? it.mood_before,
              mood_after: serverRow?.mood_after ?? it.mood_after,
              embedding_status: serverRow?.embedding_status ?? it.embedding_status,
            }
          : it
      );
      setItems(nextItems);
      scheduleNextPoll(nextItems);
    },
    [items, scheduleNextPoll]
  );

  return (
    <div className="w-full max-w-3xl mx-auto px-0 sm:px-2 pt-4 sm:pt-6 pb-24 overflow-x-hidden">
      <style>{`
        @keyframes voicePendingPulse {
          0%   { box-shadow: 0 0 0 0 ${AMBER}80; }
          70%  { box-shadow: 0 0 0 5px ${AMBER}00; }
          100% { box-shadow: 0 0 0 0 ${AMBER}00; }
        }
        @media (prefers-reduced-motion: reduce) {
          [style*="voicePendingPulse"] { animation: none !important; }
        }
      `}</style>
      <PageHeader {...headerProps} search={search} onSearch={setSearch} recordRef={recordRef} />

      {/* The strip renders itself away when no note carries a label on both
          sides, so it needs no condition of its own beyond "there is a list". */}
      {stripSummary && (
        <div
          ref={stripRef}
          className="mb-6 sm:mb-8 rounded-2xl sm:rounded-3xl bg-white dark:bg-stone-800/40 border border-stone-200/70 dark:border-stone-700/60 shadow-sm"
        >
          <button
            type="button"
            onClick={() => setStripOpen((v) => !v)}
            aria-expanded={stripOpen}
            className="w-full flex items-center gap-3 px-5 py-4 text-left rounded-2xl sm:rounded-3xl focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-300"
          >
            <span className="flex-1 min-w-0">
              <span className="block text-base font-semibold text-stone-800 dark:text-[#F0EDE8]">
                How speaking shifts you
              </span>
              <span className="block text-sm text-stone-500 dark:text-stone-400 mt-0.5">
                <span
                  className="font-bold tabular-nums"
                  style={{ color: stripSummary.shift >= 0 ? SAGE : '#B45309' }}
                >
                  {stripSummary.shift > 0 ? '+' : ''}
                  {stripSummary.shift.toFixed(1)}
                </span>{' '}
                {stripSummary.labelled === stripSummary.total
                  ? 'across your last 30 days'
                  : `from ${stripSummary.labelled} of ${stripSummary.total} notes you labelled`}
              </span>
            </span>
            <span
              aria-hidden="true"
              className="text-stone-400 transition-transform duration-300 ease-out motion-reduce:transition-none"
              style={{ transform: stripOpen ? 'rotate(180deg)' : 'none' }}
            >
              ▾
            </span>
          </button>
          {stripOpen && (
          <div className="px-4 sm:px-5 pb-5">
        <EchoesDashboard
          showIntro={false}
          echoes={items}
          onViewTimeline={() => {
            const list = document.getElementById('voice-timeline');
            list?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
        />
          </div>
          )}
        </div>
      )}

      {loading && items.length === 0 && (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-24 rounded-3xl bg-stone-100/70 dark:bg-stone-800/40 animate-pulse"
            />
          ))}
        </div>
      )}

      {error && (
        <div
          className="rounded-2xl border p-4 text-sm"
          style={{ backgroundColor: `${AMBER}10`, borderColor: `${AMBER}40`, color: '#78350f' }}
        >
          {error}
        </div>
      )}

      {!loading && !error && items.length === 0 && (
        <EmptyState onRecord={onRecord} />
      )}

      {items.length > 0 && filteredItems.length === 0 && search && (
        <NoResultsState query={search} onClear={() => setSearch('')} />
      )}

      <VoiceTour
        open={tourOpen}
        anchors={tourAnchors}
        onClose={() => setTourOpen(false)}
      />

      {filteredItems.length > 0 && (
        <div id="voice-timeline" className="space-y-7 sm:space-y-10">
          {grouped.map(([date, dayItems], gi) => (
            <section key={date}>
              <h2 className="text-[11px] sm:text-xs uppercase tracking-[0.16em] font-semibold text-stone-400 dark:text-stone-500 mb-2 sm:mb-3 px-1">
                {dateHeader(date)}
              </h2>
              <div className="space-y-3 sm:space-y-4">
                {dayItems.map((note, ni) => (
                  // One renderer for both kinds. `refresh` is a stable
                  // useCallback on purpose — VoiceCard is memoised, and an
                  // inline arrow here would hand every card a new identity on
                  // each poll tick, defeating it.
                  <div key={note.id} ref={gi === 0 && ni === 0 ? firstCardRef : undefined}>
                  <VoiceCard
                    note={note}
                    onDeleted={handleDeleted}
                    onMoodChanged={handleMoodChanged}
                    onEdited={refresh}
                    onAccepted={refresh}
                    onOpenJournal={onOpenJournal}
                  />
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

    </div>
  );
}

